import { html } from '../lib/html.js';
import { onlyDigits } from '../lib/masks.js';
import { applyApiFieldErrors, busy, clearFieldError, setFieldError, toastError, toastSuccess } from '../lib/ui.js';
import { accessRequests, authorizations } from '../services/authorizations.js';
import { Page, go } from './base.js';
import { vehicleId } from '../services/vehicles.js';

/**
 * /autorizacao — o mecânico lê o QR do dono ou digita o código de 6 dígitos.
 * A câmera usa um leitor em JavaScript (carregado só nesta tela). Sem câmera ou sem permissão,
 * fica só o campo do código.
 * Sem o dono por perto: pede o acesso pela placa (ou número de série) e o dono responde no app dele.
 */
class AuthorizeScanPage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/workshop" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Receber veículo</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <div class="narrow">
          <div class="scanner">
            <video playsinline muted></video>
            <div class="scanner-frame" aria-hidden="true"></div>
            <p class="scanner-msg">Abrindo a câmera...</p>
          </div>
          <p class="hint small ion-text-center">Aponte para o QR Code no celular do dono do veículo.</p>
          <form novalidate class="code-form">
            <ion-list lines="none" class="form">
              <ion-input name="code" label="Ou digite o código de 6 dígitos" label-placement="floating" fill="outline" inputmode="numeric" maxlength="7" class="code-input"></ion-input>
            </ion-list>
            <ion-button type="submit" expand="block" class="submit biz">Liberar veículo</ion-button>
          </form>
          <form novalidate class="request-form">
            <h3 class="section-title">O dono não está por perto?</h3>
            <p class="hint small request-hint">Peça o acesso pela placa. O dono recebe o pedido no app dele e aceita ou recusa.</p>
            <ion-list lines="none" class="form">
              <ion-input name="identifier" label="Placa ou número de série" label-placement="floating" fill="outline" maxlength="30" autocapitalize="characters" autocomplete="off"></ion-input>
            </ion-list>
            <ion-button type="submit" expand="block" fill="outline" class="request biz">Pedir acesso ao dono</ion-button>
          </form>
        </div>
      </ion-content>`;
  }

  setup() {
    const input = this.$('ion-input[name="code"]');
    input.addEventListener('ionInput', (e) => {
      const d = onlyDigits(e.target.value).slice(0, 6);
      e.target.value = d.length > 3 ? `${d.slice(0, 3)} ${d.slice(3)}` : d;
      if (d.length === 6) this.activate({ code: d });
    });
    this.$('form.code-form').addEventListener('submit', (e) => {
      e.preventDefault();
      const d = onlyDigits(input.value);
      if (d.length !== 6) return setFieldError(input, 'Digite os 6 números');
      this.activate({ code: d });
    });
    const identifier = this.$('ion-input[name="identifier"]');
    identifier.addEventListener('ionInput', (e) => {
      e.target.value = String(e.target.value ?? '').toUpperCase().replace(/[^A-Z0-9 -]/g, '');
      clearFieldError(e.target);
    });
    this.$('form.request-form').addEventListener('submit', (e) => {
      e.preventDefault();
      this.request(identifier);
    });
    this.addEventListener('ionViewWillLeave', () => this.stopCamera());
  }

  onEnter() {
    this.busy = false;
    this.$('ion-input[name="code"]').value = '';
    this.$('ion-input[name="identifier"]').value = '';
    clearFieldError(this.$('ion-input[name="identifier"]'));
    this.startCamera();
    // a validade do pedido é definida no portal admin: o texto só cita as horas depois de saber quantas são
    accessRequests
      .sent()
      .then((r) => (this.$('.request-hint').textContent = `Peça o acesso pela placa. O dono recebe o pedido no app dele e tem ${hoursText(r.requestTtlHours)} para aceitar ou recusar.`))
      .catch(() => {});
  }

  /** Pedido pela placa (ou série): o veículo só aparece para a empresa depois que o dono aceitar. */
  async request(input) {
    const identifier = String(input.value ?? '').replace(/[^A-Z0-9]/g, '');
    if (identifier.length < 5) return setFieldError(input, 'Digite a placa ou o número de série');
    await busy(this.$('.request'), async () => {
      try {
        const { request } = await accessRequests.create(identifier);
        toastSuccess(`Pedido enviado ao dono de ${request.label}. Avisamos quando ele responder.`);
        this.stopCamera();
        go('/oficina/veiculos', 'root');
      } catch (err) {
        if (err.code === 'VEHICLE_NOT_FOUND' || err.code === 'REQUEST_ALREADY_OPEN' || err.code === 'REQUEST_COOLDOWN') return setFieldError(input, err.message);
        if (!applyApiFieldErrors(this, err.details)) toastError(err.message);
      }
    });
  }

  async startCamera() {
    const msg = this.$('.scanner-msg');
    const video = this.$('video');
    msg.hidden = false;
    msg.textContent = 'Abrindo a câmera...';
    try {
      const { default: QrScanner } = await import('qr-scanner');
      if (!(await QrScanner.hasCamera())) throw new Error('sem câmera');
      this.scanner = new QrScanner(video, (r) => this.onScan(r.data), { preferredCamera: 'environment', maxScansPerSecond: 8, returnDetailedScanResult: true });
      await this.scanner.start();
      msg.hidden = true;
    } catch (err) {
      msg.textContent = /Permission|NotAllowed/i.test(String(err?.name ?? err)) ? 'Sem permissão para usar a câmera. Digite o código abaixo.' : 'Câmera indisponível. Digite o código abaixo.';
    }
  }

  stopCamera() {
    this.scanner?.stop();
    this.scanner?.destroy();
    this.scanner = null;
  }

  onScan(data) {
    if (this.busy) return;
    if (!String(data).startsWith('TCAUTH1:')) {
      this.$('.scanner-msg').hidden = false;
      this.$('.scanner-msg').textContent = 'Esse QR não é de uma autorização TicketCar.';
      return;
    }
    this.scanner?.pause();
    this.activate({ token: data });
  }

  async activate(body) {
    if (this.busy) return;
    this.busy = true;
    await busy(this.$('.submit'), async () => {
      try {
        const r = await authorizations.activate(body);
        toastSuccess(`Liberado: ${[vehicleId(r.vehicle), r.vehicle.model].filter(Boolean).join(' · ') || 'veículo'}`);
        this.stopCamera();
        go(`/oficina/veiculos/${r.vehicle.id}`, 'root');
      } catch (err) {
        toastError(err.message);
        this.$('ion-input[name="code"]').value = '';
        this.busy = false;
        setTimeout(() => this.scanner?.start().catch(() => {}), 1500);
      }
    });
  }
}

const hoursText = (h) => (h === 1 ? '1 hora' : `${h} horas`);

customElements.define('page-authorize-scan', AuthorizeScanPage);
